import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class DceRegistryService {
  private readonly loadPromises = new Map<string, Promise<void>>();

  /** @param asModule Pass `false` for classic bundles that self-locate via `document.currentScript`. */
  loadBundle(bundleUrl: string, asModule = true): Promise<void> {
    if (!bundleUrl) {
      return Promise.reject(new Error('DCE bundle URL is empty.'));
    }

    const existingPromise = this.loadPromises.get(bundleUrl);
    if (existingPromise) {
      return existingPromise;
    }

    const loadPromise = this.loadScript(bundleUrl, asModule);
    this.loadPromises.set(bundleUrl, loadPromise);
    return loadPromise;
  }

  private loadScript(bundleUrl: string, asModule: boolean): Promise<void> {
    const existingScript = document.querySelector<HTMLScriptElement>(`script[src="${bundleUrl}"]`);
    if (existingScript) {
      if (existingScript.dataset['dceLoaded'] === 'true') {
        return Promise.resolve();
      }

      return new Promise<void>((resolve, reject) => {
        existingScript.addEventListener('load', () => resolve(), { once: true });
        existingScript.addEventListener(
          'error',
          () => reject(new Error(`Unable to load DCE bundle from ${bundleUrl}`)),
          { once: true }
        );
      });
    }

    return new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = bundleUrl;
      // Angular dev/prod bundles are ESM; load as module for proper execution.
      // Classic third-party bundles are loaded without type="module" instead, so
      // document.currentScript remains available for their own asset self-location.
      if (asModule) {
        script.type = 'module';
      }
      script.onload = () => {
        script.dataset['dceLoaded'] = 'true';
        resolve();
      };
      script.onerror = () => reject(new Error(`Unable to load DCE bundle from ${bundleUrl}`));
      document.head.appendChild(script);
    });
  }
}
